// Product technical specifications — researched industry-average values.
// These enrich the /collection/:productId Product Detail Pages.
//
// Values are indicative Australian-market averages for premium residential
// applications. All ratings/units:
//   - AS 4586 / AS 4663:  Australian standards for slip resistance
//   - PEI:                Porcelain Enamel Institute (tile wear rating)
//   - Janka:              Timber hardness (higher = harder)
//   - GSM:                grams per square metre (carpet weight)
//   - oz/yd²:             ounces per square yard (imperial carpet weight)
//
// Structure per product:
//   { origin, format, coreSpecs[], care[], provenance, bestUsed[],
//     warranty, sustainability, priceBand }
//
// Missing entries fall back to sensible category defaults in
// pages/ProductDetail.jsx.

// ---- Category defaults (used if a product doesn't override) ---------

export const CATEGORY_DEFAULTS = {
  Timber: {
    coreSpecs: [
      { label: "Species", value: "Oak" },
      { label: "Construction", value: "Engineered · birch/poplar plywood core" },
      { label: "Wear layer", value: "4 mm sawn oak veneer" },
      { label: "Total thickness", value: "15 mm" },
      { label: "Board width", value: "220 mm (wide-plank)" },
      { label: "Board length", value: "up to 2200 mm" },
      { label: "Grade", value: "ABCD · natural character" },
      { label: "Surface treatment", value: "Wire-brushed · UV-cured matte oil" },
      { label: "Janka hardness", value: "4.9 kN (oak reference)" },
      { label: "Install method", value: "Glue-down or floating (T&G click)" },
      { label: "Sub-floor readings", value: "≤ 12% MC, ≤ 75% RH" },
      { label: "Compatible with underfloor heating", value: "Yes · max 27 °C surface" },
    ],
    care: [
      "Vacuum weekly with soft-brush attachment or micro-fibre mop dry.",
      "Damp-mop with a pH-neutral timber cleaner every 2–4 weeks — never wet-mop or use vinegar/ammonia.",
      "Wipe spills within 60 seconds — timber tolerates moisture briefly, not standing water.",
      "Re-oil high-traffic areas every 3–5 years to refresh matte finish.",
      "Sand & recoat the whole floor once every 15–25 years — engineered floors accept 2–3 sandings.",
    ],
    provenance:
      "Oak (Quercus robur / Quercus petraea) harvested under FSC or PEFC chain-of-custody. Sawn, kiln-dried and pressed onto stable birch-poplar plywood cores in Europe (predominantly Poland, Croatia and Serbia), then finished with a wire brush and matte oil for a soft, tactile surface that hides fine wear.",
    bestUsed: ["Living rooms", "Bedrooms", "Kitchens (with mats at wet zones)", "Hallways", "Home offices"],
    warranty: "25-year residential structural · 10-year residential wear",
    sustainability: "FSC/PEFC-certified oak · low-VOC UV-cured finish · engineered format uses ~60% less prime oak than solid boards",
    priceBand: "$$$",
  },

  Carpet: {
    coreSpecs: [
      { label: "Fibre content", value: "100% New Zealand wool" },
      { label: "Construction", value: "Loop pile" },
      { label: "Pile weight", value: "1400 gsm · 41 oz/yd²" },
      { label: "Pile height", value: "8 mm" },
      { label: "Yarn twist", value: "4.5 turns per inch (heat-set)" },
      { label: "Primary backing", value: "Woven polypropylene" },
      { label: "Secondary backing", value: "Natural jute" },
      { label: "Roll width", value: "3.66 m (standard broadloom)" },
      { label: "Underlay", value: "Recommended: 10 mm rebond or wool felt" },
      { label: "Fire rating", value: "AS/ISO 9239.1 · Critical Radiant Flux ≥ 2.2 kW/m²" },
    ],
    care: [
      "Vacuum 2–3× per week with a suction-only or adjustable-height beater bar (wool prefers gentle).",
      "Blot spills immediately with a clean white cloth — never rub; work from the outside in.",
      "Professional hot-water extraction clean every 12–18 months (wool-safe detergent only).",
      "Rotate furniture positions every 12 months to even out pile compression.",
      "Wool naturally resists staining and self-extinguishes — avoid over-cleaning or harsh chemicals.",
    ],
    provenance:
      "New Zealand strong-wool from South Island high-country flocks — the world's whitest, coarsest, and most stain-resistant wool. Yarn is spun and dyed in NZ or Australia, tufted or woven on European looms, and finished with natural-fibre backings for full biodegradability at end-of-life.",
    bestUsed: ["Bedrooms", "Living rooms", "Home cinemas", "Hallways (with runners)", "Studies"],
    warranty: "15-year residential wear · 10-year stain resistance",
    sustainability: "100% biodegradable · Renewable natural fibre · GECA / GreenTag certified where indicated · Wool is the only inherently flame-resistant natural fibre",
    priceBand: "$$$",
  },

  Tile: {
    coreSpecs: [
      { label: "Material", value: "Full-body porcelain" },
      { label: "Format", value: "600 × 600 mm (standard)" },
      { label: "Thickness", value: "10 mm" },
      { label: "Finish", value: "Honed matte" },
      { label: "PEI wear rating", value: "PEI IV (heavy residential / light commercial)" },
      { label: "Slip resistance (dry)", value: "AS 4586 · P3 / R10" },
      { label: "Slip resistance (wet)", value: "AS 4586 · P4 / R10" },
      { label: "Water absorption", value: "≤ 0.5% (Group BIa · frost-resistant)" },
      { label: "Breaking strength", value: "≥ 1300 N (AS 4459.4)" },
      { label: "Grout joint", value: "3 mm recommended · rectified edges" },
      { label: "Compatible with underfloor heating", value: "Yes" },
    ],
    care: [
      "Sweep or vacuum daily to remove abrasive grit that scratches finish.",
      "Damp-mop with pH-neutral cleaner weekly — avoid acidic or bleach-based products on honed finishes.",
      "Seal cementitious grout once at install and refresh every 3–5 years to prevent staining.",
      "For polished porcelain, re-polish with diamond-pad service every 8–10 years.",
      "Wipe up citrus, wine and coffee within minutes to protect grout lines.",
    ],
    provenance:
      "Fired at 1220 °C in Italian, Spanish or Australian kilns from a body of kaolin, feldspar and quartz. Full-body porcelain carries the pattern through the entire tile thickness (not just a printed surface), so chips and edges retain the intended finish over decades.",
    bestUsed: ["Kitchens", "Bathrooms (with correct slip rating)", "Living areas", "Alfresco (frost-safe)", "Wet rooms"],
    warranty: "Lifetime material warranty · 10-year colour/wear",
    sustainability: "Contains 20–40% recycled content · Cradle-to-cradle recyclable at end of life · Zero-VOC surface",
    priceBand: "$$",
  },

  Stone: {
    coreSpecs: [
      { label: "Material", value: "Natural limestone" },
      { label: "Format", value: "600 × 400 mm (standard slab-cut)" },
      { label: "Thickness", value: "20 mm" },
      { label: "Finish", value: "Honed" },
      { label: "Density", value: "2500–2700 kg/m³" },
      { label: "Water absorption", value: "0.2–2.5% (varies by stone)" },
      { label: "Compressive strength", value: "≥ 100 MPa" },
      { label: "Slip resistance (honed)", value: "AS 4586 · P3 / R10" },
      { label: "Slip resistance (leathered/tumbled)", value: "AS 4586 · P4 / R11" },
      { label: "Compatible with underfloor heating", value: "Yes · natural stone is an excellent thermal conductor" },
      { label: "Sealer required", value: "Yes · impregnating penetrating sealer" },
    ],
    care: [
      "Seal on install and refresh sealer every 12–24 months for high-traffic areas.",
      "Sweep or vacuum daily; damp-mop with a pH-neutral stone cleaner weekly.",
      "Never use vinegar, lemon, bleach or ammonia — all etch calcareous stones (limestone, marble, travertine).",
      "Wipe wine, citrus, oil and coffee within seconds to prevent etching.",
      "Have honed finishes re-honed by a stone-restoration professional every 10–15 years to remove wear scratches.",
    ],
    provenance:
      "Quarried from natural stone beds in Europe, Türkiye or Australia, cut into slabs by wire or gang-saw, then finished by machine or hand. Every piece carries the unique veining, fossils and colour of its bed — no two tiles are identical.",
    bestUsed: ["Kitchens", "Entries & foyers", "Bathrooms (with correct slip rating)", "Fireplace surrounds", "Feature walls"],
    warranty: "Material only · workmanship of installer covered separately",
    sustainability: "Natural, quarried material · zero VOCs · lifetime durability · fully recyclable as crushed aggregate at end of life",
    priceBand: "$$$$",
  },
};

// ---- Per-product overrides -----------------------------------------

export const PRODUCT_SPECS = {
  // ==================== TIMBER ====================
  "tmb-luxplank": {
    coreSpecs: [
      { label: "Species", value: "Oak" },
      { label: "Construction", value: "Engineered · 3-ply cross-bonded" },
      { label: "Wear layer", value: "4 mm sawn oak" },
      { label: "Total thickness", value: "15 mm" },
      { label: "Board width", value: "220 mm wide-plank" },
      { label: "Board length", value: "up to 2200 mm (mixed lengths)" },
      { label: "Grade", value: "ABCD · natural oak character" },
      { label: "Surface treatment", value: "Wire-brushed · matte oil (low-sheen)" },
      { label: "Colour count", value: "10 curated tones" },
      { label: "Janka hardness", value: "4.9 kN" },
      { label: "Install method", value: "Glue-down (recommended) or T&G floating" },
      { label: "Underfloor heating", value: "Yes · max 27 °C surface" },
    ],
    provenance:
      "The studio's signature engineered oak range. Sawn from FSC-certified oak, pressed onto a cross-bonded plywood core for dimensional stability across Sydney's humidity range, and finished with a wire brush that opens the grain before a UV-cured matte oil is drawn in. Ten tones — from bleached Scandi to near-black Mono — allow one range to carry an entire home.",
    bestUsed: ["Whole-house specification", "Living rooms", "Bedrooms", "Kitchens", "Hallways"],
  },

  "tmb-versailles-parquet": {
    coreSpecs: [
      { label: "Species", value: "Oak" },
      { label: "Construction", value: "Engineered parquet panel · pre-assembled" },
      { label: "Panel size", value: "900 × 900 mm (standard Versailles)" },
      { label: "Overall thickness", value: "18 mm" },
      { label: "Wear layer", value: "6 mm solid oak" },
      { label: "Geometry", value: "16th-century Versailles pattern · bespoke variants on request" },
      { label: "Surface treatment", value: "Wire-brushed · matte oil or hard-wax finish" },
      { label: "Colour options", value: "5 tones (Honey · Champagne · Fumed · Smoked · Rustic)" },
      { label: "Janka hardness", value: "4.9 kN" },
      { label: "Install method", value: "Full-spread glue-down only" },
      { label: "Underfloor heating", value: "Yes · specification with installer" },
    ],
    provenance:
      "The historic Versailles panel — first laid at the Château de Versailles in 1684 to replace marble that was buckling from the daily mopping-out of the palace — recut for modern engineered construction. Each pre-assembled 900 mm panel comprises hand-fitted oak segments in a woven diamond geometry.",
    bestUsed: ["Formal entries", "Living rooms", "Dining rooms", "Grand hallways", "Feature rooms"],
    priceBand: "$$$$",
    warranty: "25-year residential structural · 10-year finish",
  },

  "tmb-euro-oak-natural": {
    coreSpecs: [
      { label: "Species", value: "Oak" },
      { label: "Construction", value: "Engineered · 3-ply" },
      { label: "Wear layer", value: "4 mm" },
      { label: "Total thickness", value: "14 mm" },
      { label: "Board width", value: "190 mm" },
      { label: "Board length", value: "up to 1900 mm" },
      { label: "Grade", value: "AB · minimal knots" },
      { label: "Surface treatment", value: "Wire-brushed · matte oil" },
      { label: "Colour", value: "Untinted natural oak" },
      { label: "Janka hardness", value: "4.9 kN" },
    ],
  },

  "tmb-euro-oak-smoked": {
    coreSpecs: [
      { label: "Species", value: "Oak · fumed" },
      { label: "Construction", value: "Engineered · 3-ply" },
      { label: "Wear layer", value: "4 mm" },
      { label: "Total thickness", value: "14 mm" },
      { label: "Board width", value: "220 mm" },
      { label: "Surface treatment", value: "Ammonia-fumed for full-depth colour · wire-brushed · matte oil" },
      { label: "Colour", value: "Smoked charcoal to walnut brown" },
      { label: "Janka hardness", value: "4.9 kN (hardness preserved through fuming)" },
    ],
    provenance:
      "Fuming exposes raw oak to ammonia vapour in a sealed chamber for 24–72 hours, allowing the vapour to react with tannins throughout the plank — a full-depth colour treatment that never fades or wears off (unlike surface stains). Originally used in 19th-century English stables to keep dark timber consistent when it eventually got sanded.",
  },

  "tmb-walnut-herringbone": {
    coreSpecs: [
      { label: "Species", value: "American Black Walnut" },
      { label: "Construction", value: "Engineered herringbone block · 3-ply" },
      { label: "Block size", value: "600 × 120 mm × 14 mm" },
      { label: "Wear layer", value: "4 mm solid walnut" },
      { label: "Pattern", value: "Traditional 90° herringbone (double or single row)" },
      { label: "Surface treatment", value: "Brushed · matte lacquer" },
      { label: "Colour", value: "Rich chocolate walnut, deepens over time" },
      { label: "Janka hardness", value: "4.5 kN (walnut is slightly softer than oak)" },
      { label: "Install method", value: "Full-spread glue-down only" },
    ],
    provenance:
      "American Black Walnut from the eastern United States forestry belt — the classic dark-hardwood floor of Manhattan brownstones and Parisian apartments. Naturally chocolate-toned with dramatic figure and cathedral grain, pre-cut into engineered herringbone blocks for glue-down install.",
    bestUsed: ["Statement living rooms", "Dining rooms", "Studies", "Hotel-style bedrooms"],
    priceBand: "$$$$",
  },

  "tmb-oak-chevron": {
    coreSpecs: [
      { label: "Species", value: "Oak" },
      { label: "Construction", value: "Engineered chevron block · 3-ply" },
      { label: "Block size", value: "600 × 90 mm × 14 mm · 45° or 60° cut" },
      { label: "Wear layer", value: "4 mm sawn oak" },
      { label: "Pattern", value: "Chevron (V-pattern with mitred ends)" },
      { label: "Surface treatment", value: "Wire-brushed · matte oil" },
      { label: "Cut angles", value: "45° (Parisian) · 60° (Hungarian)" },
      { label: "Janka hardness", value: "4.9 kN" },
      { label: "Install method", value: "Full-spread glue-down only" },
    ],
    provenance:
      "The Parisian chevron — mitred oak blocks that meet in a continuous V-line, distinct from herringbone's stepped 90° pattern. Refined in the ateliers of 18th-century Paris and still associated with Haussmann-era apartments. Every block is pre-cut at 45° or 60° and installed with tight, glue-line-only joins.",
    bestUsed: ["Formal entries", "Living rooms", "Dining rooms", "Statement rooms"],
    priceBand: "$$$$",
  },

  "tmb-blackbutt": {
    coreSpecs: [
      { label: "Species", value: "Australian Blackbutt (Eucalyptus pilularis)" },
      { label: "Construction", value: "Engineered · 3-ply Australian hardwood core" },
      { label: "Wear layer", value: "4 mm" },
      { label: "Total thickness", value: "14 mm" },
      { label: "Board width", value: "180 mm" },
      { label: "Surface treatment", value: "Water-based UV lacquer" },
      { label: "Colour", value: "Pale golden brown with pink undertone" },
      { label: "Janka hardness", value: "9.1 kN (nearly double that of oak)" },
      { label: "Fire rating", value: "BAL-29 compliant (bushfire-attack level)" },
    ],
    provenance:
      "Blackbutt is the workhorse hardwood of the Australian east coast — a tall eucalyptus native to NSW and southern QLD. Its charcoal-black bark (hence 'blackbutt') sheds after bushfires while the timber inside stays golden. One of the six Australian hardwood species approved for BAL-29 bushfire-zone construction.",
    bestUsed: ["Whole-house Australian architecture", "Bushfire-zone builds", "Hallways", "Living rooms"],
    sustainability: "Sourced from PEFC-certified NSW sustainable forestry · low food-miles for Australian projects · BAL-29 approved",
  },

  // ==================== CARPET ====================
  "cpt-bremworth-galet-skarn": {
    coreSpecs: [
      { label: "Manufacturer", value: "Bremworth (New Zealand)" },
      { label: "Fibre content", value: "100% NZ wool" },
      { label: "Construction", value: "Cut-loop with textured surface" },
      { label: "Pile weight", value: "1580 gsm · 46 oz/yd²" },
      { label: "Pile height", value: "9 mm" },
      { label: "Primary backing", value: "Woven polypropylene" },
      { label: "Roll width", value: "3.66 m" },
      { label: "Wool origin", value: "South Island NZ (Merino / Romney cross)" },
      { label: "Fire rating", value: "AS/ISO 9239.1 · CRF ≥ 4.5 kW/m²" },
    ],
    provenance:
      "Bremworth is New Zealand's heritage wool-carpet maker (founded 1958), producing tufted broadloom from South Island strong-wool. 'Galet Skarn' pairs a stone-grey base with metamorphic mineral tones — a nod to the metamorphic rock formations of the Southern Alps.",
    bestUsed: ["Bedrooms", "Home cinemas", "Living rooms", "Studies"],
    priceBand: "$$$$",
  },

  "cpt-highland-loop": {
    coreSpecs: [
      { label: "Fibre content", value: "100% NZ wool" },
      { label: "Construction", value: "Textured loop" },
      { label: "Pile weight", value: "1300 gsm · 38 oz/yd²" },
      { label: "Pile height", value: "7 mm" },
      { label: "Roll width", value: "3.66 m" },
      { label: "Palette", value: "Highland-inspired heather tones" },
      { label: "Fire rating", value: "AS/ISO 9239.1 · CRF ≥ 2.2 kW/m²" },
    ],
    provenance:
      "A tighter, harder-wearing loop pile with a subtle heathered mélange — spun from multiple wool tones then twisted for depth of colour that hides marks and shading. Suited to open-plan living where a plush plush pile would flatten over time.",
    bestUsed: ["Living rooms", "Family rooms", "Home offices", "Hallways"],
  },

  "cpt-sisal-hybrid": {
    coreSpecs: [
      { label: "Fibre content", value: "80% wool · 20% sisal (natural blend)" },
      { label: "Construction", value: "Bouclé loop" },
      { label: "Pile weight", value: "1100 gsm · 32 oz/yd²" },
      { label: "Backing", value: "Latex-bonded natural jute" },
      { label: "Roll width", value: "4.0 m" },
      { label: "Palette", value: "Natural cream to grey-taupe" },
    ],
    provenance:
      "A hybrid weave that gives the tactile softness of wool with sisal's dry, linear texture and moisture resistance — the studio's recommendation for Sydney coastal homes where 100% sisal can feel harsh underfoot.",
  },

  "cpt-cashmere-sublime": {
    coreSpecs: [
      { label: "Fibre content", value: "70% wool · 30% cashmere blend" },
      { label: "Construction", value: "Cut pile · plush" },
      { label: "Pile weight", value: "1650 gsm · 48 oz/yd²" },
      { label: "Pile height", value: "12 mm" },
      { label: "Yarn twist", value: "3.5 tpi (looser for softness)" },
    ],
    provenance:
      "Cashmere blended into premium wool creates the softest tactile finish available on a residential carpet — the closest floor-covering equivalent to walking on a folded blanket. Reserved for bedrooms and cinema rooms where softness matters more than raw durability.",
    bestUsed: ["Master bedrooms", "Home cinemas", "Nurseries"],
    priceBand: "$$$$",
  },

  "cpt-berber-loop": {
    coreSpecs: [
      { label: "Fibre content", value: "100% NZ wool" },
      { label: "Construction", value: "Level Berber loop" },
      { label: "Pile weight", value: "1420 gsm · 42 oz/yd²" },
      { label: "Pile height", value: "6 mm (tight loop)" },
      { label: "Palette", value: "Off-white flecked with natural grey/brown" },
      { label: "Fire rating", value: "AS/ISO 9239.1 · CRF ≥ 4.5 kW/m²" },
    ],
    provenance:
      "The traditional Berber loop — named after the Berber-Amazigh weavers of the Atlas Mountains who first knotted this dense, flecked pattern. Its tight, uncut loops resist crushing and shading, making it the most durable domestic wool carpet available.",
    bestUsed: ["High-traffic living rooms", "Family rooms", "Media rooms"],
  },

  "cpt-sisal-natural": {
    coreSpecs: [
      { label: "Fibre content", value: "100% sisal (agave sisalana)" },
      { label: "Construction", value: "Flat-weave · herringbone or bouclé options" },
      { label: "Pile weight", value: "1900 gsm · 56 oz/yd²" },
      { label: "Backing", value: "Latex-bonded natural jute" },
      { label: "Colour", value: "Natural cream · unbleached" },
    ],
    provenance:
      "Sisal is the leaf-fibre of the agave sisalana plant — grown in Brazil, Tanzania and Kenya, cut, retted and spun into hard-wearing coarse yarn. Naturally flame-resistant, hypoallergenic, and biodegradable. Feels harder underfoot than wool but ages beautifully over decades.",
    sustainability: "100% natural plant fibre · fully compostable · no dyes or synthetic backings on the natural range",
    priceBand: "$$",
  },

  // ==================== TILE ====================
  "tle-botticino": {
    coreSpecs: [
      { label: "Material", value: "Full-body porcelain (marble-look)" },
      { label: "Format", value: "600 × 1200 mm large-format" },
      { label: "Thickness", value: "10 mm" },
      { label: "Finish", value: "Honed matte" },
      { label: "Inspired by", value: "Botticino marble (Brescia, Italy)" },
      { label: "PEI rating", value: "PEI IV" },
      { label: "Slip resistance (wet)", value: "AS 4586 · P4 / R10" },
      { label: "Water absorption", value: "≤ 0.05% (Group BIa)" },
      { label: "Rectified edge", value: "Yes · 3 mm minimum grout" },
    ],
    provenance:
      "Digitally printed porcelain replicating Botticino, the classic warm-white Italian marble from the hills above Brescia — the same stone specified for the Duomo di Milano's floors. Porcelain gives you the marble look with none of the etching risk from citrus or wine.",
  },

  "tle-calacatta-slab": {
    coreSpecs: [
      { label: "Material", value: "Sintered porcelain slab" },
      { label: "Format", value: "1600 × 3200 mm slab · book-matchable" },
      { label: "Thickness", value: "12 mm" },
      { label: "Finish", value: "Polished · light-grey/gold veining" },
      { label: "Inspired by", value: "Calacatta Oro marble (Carrara, Italy)" },
      { label: "PEI rating", value: "PEI III (feature-wall or moderate-traffic floor)" },
      { label: "Water absorption", value: "≤ 0.05%" },
    ],
    provenance:
      "The Calacatta family is the elite of Carrara marbles — quarried from a small vein above Massa. Sintered porcelain slabs at 1600×3200 mm allow the same book-matched veining architects specify in stone, but with lifetime resistance to etching and staining.",
    bestUsed: ["Feature walls", "Kitchen splashbacks", "Bathroom walls", "Kitchen islands (as benchtop)"],
    priceBand: "$$$",
  },

  "tle-terrazzo": {
    coreSpecs: [
      { label: "Material", value: "Custom poured cementitious terrazzo (or porcelain terrazzo tile alternative)" },
      { label: "Format", value: "Poured seamless · or 600 × 600 mm porcelain tile" },
      { label: "Thickness", value: "40 mm (poured) · 10 mm (tile)" },
      { label: "Aggregate", value: "Marble · quartz · granite chip (5-30 mm)" },
      { label: "Finish", value: "Honed and sealed" },
      { label: "Slip resistance (honed wet)", value: "AS 4586 · P3 / R10" },
      { label: "Colour options", value: "5 studio colours (custom base + aggregate combinations available)" },
      { label: "Underfloor heating", value: "Yes (verify with poured-terrazzo installer)" },
    ],
    provenance:
      "Terrazzo is a 500-year-old Venetian construction technique — poured cementitious binder studded with marble chip offcuts, ground back and polished flat. The studio's custom terrazzo lets you specify the exact aggregate palette (chip size, marble type, base tone) to match a client's brief.",
    bestUsed: ["Kitchens", "Entries & foyers", "Bathrooms (wet-rated finish)", "Feature bathrooms"],
    priceBand: "$$$$",
  },

  "tle-zellige": {
    coreSpecs: [
      { label: "Material", value: "Hand-glazed Moroccan zellige" },
      { label: "Format", value: "100 × 100 mm (also 50 × 50 mm)" },
      { label: "Thickness", value: "10-15 mm (variable, hand-cut)" },
      { label: "Finish", value: "High-gloss glazed · irregular hand-formed edges" },
      { label: "Character", value: "Every tile unique · colour bloom and lippage are integral to the finish" },
      { label: "Origin", value: "Fez, Morocco · hand-crafted from Fez clay" },
      { label: "Grout", value: "1 mm minimum · white sanded (traditional)" },
    ],
    provenance:
      "Zellige (زليج) is the terracotta-based glazed tile of Fez, Morocco — hand-cut from local clay, dipped in metal-oxide glazes, and fired in kilns fuelled by olive-pit charcoal. The signature colour bloom, pinholes and irregular edges are not defects but the hallmarks of authentic hand craft. Specify with a client who values wabi-sabi imperfection.",
    bestUsed: ["Bathroom walls", "Kitchen splashbacks", "Feature walls", "Fireplace surrounds"],
    priceBand: "$$$",
  },

  "tle-concrete-porcelain": {
    coreSpecs: [
      { label: "Material", value: "Full-body porcelain (concrete-look)" },
      { label: "Format", value: "600 × 1200 mm large-format" },
      { label: "Thickness", value: "10 mm" },
      { label: "Finish", value: "Matte cementitious texture" },
      { label: "PEI rating", value: "PEI IV" },
      { label: "Slip resistance (wet)", value: "AS 4586 · P4 / R11" },
      { label: "Water absorption", value: "≤ 0.05%" },
    ],
    provenance:
      "Digitally printed porcelain replicating the raw, cementitious look of polished concrete without the cracking, staining or laitance of a real poured slab. R11-rated for wet areas — the studio's default for indoor-outdoor open-plan houses.",
  },

  "tle-herringbone-subway": {
    coreSpecs: [
      { label: "Material", value: "Glazed ceramic subway" },
      { label: "Format", value: "75 × 300 mm subway · laid in 90° herringbone" },
      { label: "Thickness", value: "8 mm" },
      { label: "Finish", value: "Gloss glazed · bevelled edge" },
      { label: "PEI rating", value: "PEI III (wall or light-traffic floor)" },
      { label: "Water absorption", value: "3-6% (Group BIIa)" },
      { label: "Grout", value: "2-3 mm · white or contrast" },
    ],
    provenance:
      "The subway tile format was patented in 1904 for the New York City underground — 75×300 mm glazed rectangles that would stay hygienic under a century of foot traffic. Laid in herringbone, they gain a dynamic movement that offsets the tile's utilitarian roots.",
    bestUsed: ["Kitchen splashbacks", "Bathroom walls", "Laundries", "Powder rooms"],
    priceBand: "$",
  },

  // ==================== STONE ====================
  "stn-taj-mahal-quartzite": {
    coreSpecs: [
      { label: "Material", value: "Taj Mahal Quartzite (natural quartzite)" },
      { label: "Origin", value: "Brazil (Bahia state)" },
      { label: "Format", value: "600 × 400 mm slab-cut · or full-slab up to 3200 mm" },
      { label: "Thickness", value: "20 mm floor / 30 mm bench" },
      { label: "Finish", value: "Honed or leathered" },
      { label: "Density", value: "2650 kg/m³" },
      { label: "Water absorption", value: "≤ 0.2%" },
      { label: "Mohs hardness", value: "7 (harder than granite; nearly the hardness of quartz)" },
      { label: "Etch resistance", value: "High · does not etch to acids like marble does" },
      { label: "Slip resistance (honed wet)", value: "AS 4586 · P3 / R10" },
    ],
    provenance:
      "Taj Mahal is a warm-white quartzite quarried in Brazil — often specified as an alternative to Calacatta marble because it delivers the same luxurious white-with-veining palette but with quartz-level hardness and no acid-etching risk. Extremely stable underfoot and in wet zones.",
    bestUsed: ["Kitchens (floor and bench)", "Bathrooms", "Feature walls", "Entries"],
    priceBand: "$$$$",
  },

  "stn-travertine-crazy": {
    coreSpecs: [
      { label: "Material", value: "Natural travertine (Turkish or Italian)" },
      { label: "Format", value: "Random 'crazy paver' shapes 200-600 mm across" },
      { label: "Thickness", value: "20-30 mm" },
      { label: "Finish", value: "Tumbled · natural pits filled or unfilled" },
      { label: "Density", value: "2400 kg/m³" },
      { label: "Water absorption", value: "0.5-2%" },
      { label: "Slip resistance (tumbled)", value: "AS 4586 · P4 / R11" },
      { label: "Frost resistance", value: "Verify batch (unfilled = higher risk)" },
    ],
    provenance:
      "Travertine is a calcium-carbonate sediment formed at hot-spring vents. Crazy-paved offcuts assembled into a random mosaic reference Palm Springs mid-century courtyards and the Getty Villa's Roman baths. Tumbled finish gives R11 wet-rating suitable for pool surrounds.",
    bestUsed: ["Pool surrounds", "Alfresco", "Feature courtyards", "Entries", "Wet rooms"],
    priceBand: "$$$",
  },

  "stn-honed-limestone": {
    coreSpecs: [
      { label: "Material", value: "Natural limestone (Portuguese / French)" },
      { label: "Format", value: "600 × 400 mm modular or 900 × 600 mm large-format" },
      { label: "Thickness", value: "20 mm" },
      { label: "Finish", value: "Honed matte" },
      { label: "Density", value: "2200-2600 kg/m³" },
      { label: "Water absorption", value: "0.5-2.5%" },
      { label: "Slip resistance (honed wet)", value: "AS 4586 · P3 / R10" },
      { label: "Compatible with underfloor heating", value: "Yes" },
    ],
    provenance:
      "Honed limestone from European quarries — often Portuguese Moleanos or French Massangis — delivers the quiet, chalky white-cream palette specified in contemporary architecture from David Chipperfield to Kelly Wearstler. Sensitive to acids and citrus, so specification is best paired with a comprehensive care briefing to the homeowner.",
    bestUsed: ["Entries", "Living rooms", "Feature walls", "Bathrooms (with correct slip finish)"],
  },

  "stn-marble-bookmatch": {
    coreSpecs: [
      { label: "Material", value: "Natural Statuario / Calacatta marble" },
      { label: "Origin", value: "Carrara, Italy" },
      { label: "Format", value: "Full slab · 3200 × 1600 mm · book-matched pairs" },
      { label: "Thickness", value: "20 mm" },
      { label: "Finish", value: "Polished or honed" },
      { label: "Density", value: "2700 kg/m³" },
      { label: "Water absorption", value: "≤ 0.2%" },
      { label: "Care", value: "Requires impregnating sealer · avoid all acids" },
    ],
    provenance:
      "The single most storied stone in Western architecture — quarried above Carrara since Roman times. Book-matching cuts a slab in two and folds it open like a butterfly, creating a symmetrical veining pattern used for feature walls, fireplaces and monolithic kitchen islands.",
    bestUsed: ["Feature walls", "Fireplace surrounds", "Kitchen islands", "Bathroom walls"],
    priceBand: "$$$$",
  },

  "stn-basaltina": {
    coreSpecs: [
      { label: "Material", value: "Basaltina lava stone (volcanic basalt)" },
      { label: "Origin", value: "Viterbo, Italy" },
      { label: "Format", value: "600 × 400 mm or 900 × 600 mm" },
      { label: "Thickness", value: "20 mm" },
      { label: "Finish", value: "Honed matte · natural charcoal grey" },
      { label: "Density", value: "2800 kg/m³ (denser than most limestones)" },
      { label: "Water absorption", value: "≤ 0.5%" },
      { label: "Mohs hardness", value: "6" },
      { label: "Slip resistance (honed wet)", value: "AS 4586 · P3 / R10" },
      { label: "Frost resistance", value: "Excellent (volcanic origin)" },
    ],
    provenance:
      "Basaltina is a volcanic tuff quarried in the hills near Viterbo, north of Rome — the same stone Michelangelo used for the paving of the Piazza del Campidoglio in 1536. Naturally warm charcoal-grey, extraordinarily dense, and resistant to freeze-thaw cycles. A quiet, contemporary alternative to bluestone.",
    bestUsed: ["Entries", "Alfresco", "Kitchens", "Bathrooms", "Fireplace surrounds"],
    priceBand: "$$$$",
  },

  "stn-bluestone": {
    coreSpecs: [
      { label: "Material", value: "Belgian bluestone (Petit Granit)" },
      { label: "Origin", value: "Hainaut, Belgium" },
      { label: "Format", value: "600 × 400 mm or random-length paver" },
      { label: "Thickness", value: "20-30 mm" },
      { label: "Finish", value: "Honed or sandblasted · natural blue-grey" },
      { label: "Density", value: "2700 kg/m³" },
      { label: "Water absorption", value: "0.3-0.5%" },
      { label: "Slip resistance (honed wet)", value: "AS 4586 · P3 / R10" },
      { label: "Slip resistance (sandblasted wet)", value: "AS 4586 · P5 / R12" },
      { label: "Frost resistance", value: "Excellent" },
    ],
    provenance:
      "Belgian Bluestone (locally, Petit Granit) is a 350-million-year-old fossiliferous limestone from the Hainaut quarries — the traditional paving of Antwerp and Brussels. Its cool blue-grey lightens to a soft dove tone when honed, and the sandblasted finish gives an outstanding R12 wet-rating for pool surrounds and stairs.",
    bestUsed: ["Pool surrounds", "Entries", "Alfresco", "Stair treads", "Public-facing floors"],
  },
};
